/**
 * Free-text address parser for the NAP Locator address search.
 *
 * The geocoder ("geocodingService") is built around a City > Barangay > Street >
 * Number hierarchy, but it only works when the city/barangay are supplied. Users
 * rarely type clean "address, barangay, city" strings, so this module pulls the
 * municipality and barangay out of a raw free-text query by matching against the
 * known service-area boundaries (the same GeoJSON the map uses).
 *
 *   "71 BLOCK 2 PHASE 2 ESGUERRA ST ADDITION HILLS MANDALUYONG"
 *      -> { address: "71 BLOCK 2 PHASE 2 ESGUERRA ST",
 *           barangay: "Addition Hills", municipality: "Mandaluyong" }
 *
 * If a barangay is recognized we can infer the municipality even when the user
 * never typed a city. Anything we cannot recognize is left as the street address.
 */

let _indexPromise = null;

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Short city name without the "City of " prefix, uppercased. */
function shortCity(name) {
  return (name || "").replace(/^CITY OF\s+/i, "").trim();
}

/**
 * Load and cache the location index from the boundary GeoJSON files.
 * Returns { cities: [{name, short}], barangays: [{name, city, short}] },
 * each list sorted by name length (descending) so the longest match wins.
 */
export function loadLocationIndex() {
  if (_indexPromise) return _indexPromise;
  _indexPromise = Promise.all([
    fetch("/data/city_boundaries.geojson").then((r) => r.json()).catch(() => null),
    fetch("/data/barangay_boundaries.geojson").then((r) => r.json()).catch(() => null),
  ]).then(([cityGeo, brgyGeo]) => {
    const cities = [];
    const seenCity = new Set();
    for (const f of cityGeo?.features || []) {
      const name = f.properties?.city_name;
      if (!name || seenCity.has(name)) continue;
      seenCity.add(name);
      cities.push({ name, short: shortCity(name) });
    }

    const barangays = [];
    for (const f of brgyGeo?.features || []) {
      const name = f.properties?.brgy_name;
      const city = f.properties?.city_name;
      if (!name) continue;
      barangays.push({ name, city: city || "", short: shortCity(city || "") });
    }

    // Longest names first → "San Antonio" wins over a hypothetical "San".
    cities.sort((a, b) => b.short.length - a.short.length);
    barangays.sort((a, b) => b.name.length - a.name.length);
    return { cities, barangays };
  });
  return _indexPromise;
}

/**
 * Extract municipality, barangay and the remaining street address from a raw
 * free-text query using the loaded location index.
 *
 * @param {string} rawText
 * @returns {{address, barangay, municipality, matched}}
 */
export async function extractLocation(rawText) {
  const original = (rawText || "").trim();
  // Strip commas to a single normalized, uppercased working string. We keep a
  // parallel "display" copy so the returned address preserves the user's text.
  let work = original.toUpperCase().replace(/,/g, " ").replace(/\s+/g, " ").trim();

  let index;
  try {
    index = await loadLocationIndex();
  } catch {
    return { address: original, barangay: "", municipality: "", matched: false };
  }
  const { cities, barangays } = index;

  let municipality = "";
  let barangay = "";

  // --- 1. Detect an explicit city (e.g. "MANDALUYONG" / "MANDALUYONG CITY") ---
  for (const c of cities) {
    const re = new RegExp(`\\b${escapeRegex(c.short.toUpperCase())}(?:\\s+CITY)?\\b`, "i");
    if (re.test(work)) {
      municipality = c.short;
      work = work.replace(re, " ").replace(/\s+/g, " ").trim();
      break;
    }
  }

  // --- 2. Detect a barangay, preferring one inside the detected city ---
  // Two passes: when a city is known, only accept barangays in that city to
  // resolve names shared across cities (e.g. "Addition Hills"). Otherwise take
  // the longest matching barangay and infer its city.
  const tryMatchBarangay = (filterByCity) => {
    for (const b of barangays) {
      if (filterByCity && municipality && b.short.toUpperCase() !== municipality.toUpperCase()) continue;
      // Allow an optional "BRGY"/"BARANGAY" prefix in the text.
      const re = new RegExp(`\\b(?:BRGY\\.?\\s+|BARANGAY\\s+)?${escapeRegex(b.name.toUpperCase())}\\b`, "i");
      if (re.test(work)) {
        barangay = b.name;
        if (!municipality) municipality = b.short;
        work = work.replace(re, " ").replace(/\s+/g, " ").trim();
        return true;
      }
    }
    return false;
  };

  if (municipality) {
    if (!tryMatchBarangay(true)) tryMatchBarangay(false);
  } else {
    tryMatchBarangay(false);
  }

  // Clean up any leftover "BRGY"/"BARANGAY" token and stray punctuation.
  let address = work.replace(/\b(?:BRGY\.?|BARANGAY)\b/gi, " ").replace(/\s+/g, " ").trim();
  address = address.replace(/^[,\-\s]+|[,\-\s]+$/g, "").trim();

  const matched = Boolean(municipality || barangay);
  // If nothing matched, fall back to the user's original text untouched.
  return {
    address: matched ? (address || original) : original,
    barangay,
    municipality,
    matched,
  };
}

export default extractLocation;
